import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiCardView,
  BiDashboardLayout,
  BiLayoutItem,
  BiLayoutMode,
} from '@euler/model/bi/dashboard';
import { ReactNode, memo, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { BiCardKind, biCardKind } from '../../functions/biCardKind';
import {
  biCardRows,
  biPresetToCustom,
  biWrapNarrowRows,
  kBiGridGapPx,
} from '../../functions/biGridLayout';
import { biPresentation } from '../../functions/biPresentation';
import { ArrangeableGrid } from './ArrangeableGrid';
import { CustomGrid } from './CustomGrid';
import { PresetGrid } from './PresetGrid';
import { StackedGrid } from './StackedGrid';

export type BiRenderCard = (
  card: BiCardView,
  /** Undefined: as tall as its content (a KPI group fits its tiles). */
  height: number | undefined,
  /** The drag handle, on the editable desktop grid only. */
  grip?: ReactNode,
  /** Drawn under the body inside the card: the phone resize grabber. */
  footer?: ReactNode,
  /** Without a height: told the height its content needs. */
  onFitHeight?: (px: number) => void,
) => ReactNode;

/** Draws a layout at the width it is given (spec §11.3). */
export const DashboardGrid = memo(
  ({
    layout,
    revision,
    cards,
    renderCard,
    onArrange,
    onResizeNarrow,
    onStackedChange,
  }: {
    layout: BiDashboardLayout;
    revision: number;
    cards: BiCardView[];
    renderCard: BiRenderCard;
    /** Given when the viewer may drag and resize here. */
    onArrange?: (items: BiLayoutItem[]) => Promise<boolean>;
    /** Given to editors: a card's height in the phone view. */
    onResizeNarrow?: (cardId: string, rows: number) => Promise<boolean>;
    /** Whether the phone view (one card per row) is what is drawn. */
    onStackedChange?: (stacked: boolean) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    const byId = useMemo(
      () => new Map(cards.map(card => [card.id, card])),
      [cards],
    );
    const presentation = useMemo(
      () => (width ? biPresentation(layout, width) : null),
      [layout, width],
    );
    const stacked = presentation ? presentation.kind === 'stacked' : null;
    useEffect(() => {
      if (stacked !== null) onStackedChange?.(stacked);
    }, [stacked, onStackedChange]);
    // A custom grid at this width: rows too narrow for their cards wrap.
    const customItems = useMemo(
      () =>
        presentation?.kind === 'custom'
          ? biWrapNarrowRows(presentation.items, width)
          : null,
      [presentation, width],
    );
    // What the editable grid starts from: the stored items, or the preset
    // exactly as drawn now, which the first drop stores. A wrapped grid is
    // not where its stored items say, so it is not arranged here: its
    // cards move from their menus.
    const arrangeable = useMemo(() => {
      if (!onArrange || !presentation || presentation.kind === 'stacked')
        return null;
      if (presentation.kind === 'custom')
        return customItems === presentation.items ? customItems : null;
      if (layout.mode !== BiLayoutMode.Preset) return null;
      const kindOf = (id: string) => {
        const card = byId.get(id);
        return card ? biCardKind(card.viz) : BiCardKind.Chart;
      };
      const columns = presentation.columnCount;
      const cell = (width - kBiGridGapPx * (columns - 1)) / columns;
      // As the preset draws it: a KPI group as tall as its tiles wrap.
      const rowsOf = (id: string, full: boolean) => {
        const card = byId.get(id);
        return card ? biCardRows(card.viz, full ? width : cell, rpx) : null;
      };
      return biPresetToCustom(layout, columns, kindOf, rowsOf).items;
    }, [onArrange, presentation, customItems, layout, byId, width, rpx]);

    let content: ReactNode = null;
    if (arrangeable && onArrange)
      content = (
        <ArrangeableGrid
          items={arrangeable}
          revision={revision}
          width={width}
          cards={byId}
          renderCard={renderCard}
          onArrange={onArrange}
        />
      );
    else if (presentation?.kind === 'stacked')
      content = (
        <StackedGrid
          cardIds={presentation.cardIds}
          cards={byId}
          narrowRows={layout.narrowRows}
          renderCard={renderCard}
          onResize={onResizeNarrow}
        />
      );
    else if (presentation?.kind === 'preset')
      content = (
        <PresetGrid
          rows={presentation.rows}
          width={width}
          cards={byId}
          renderCard={renderCard}
        />
      );
    else if (customItems)
      content = (
        <CustomGrid
          items={customItems}
          width={width}
          cards={byId}
          renderCard={renderCard}
        />
      );
    return (
      <View onLayout={event => setWidth(event.nativeEvent.layout.width)}>
        {content}
      </View>
    );
  },
);
